import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import AlertModal from "@/components/AlertModal";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

// Icons
import { ArrowLeft, EyeOff, Eye, FileStack } from "lucide-react";

// Services
// import { deleteNew } from "@/services/news";
import { deleteLaw } from "@/services/law";

// Utils
import formatDate from "@/utils/formatDate";

// Types
import type { Law } from "@/types/law";
import { S3_BUCKET_LINK } from "@/components/constants";

export const Route = createFileRoute("/_layout-private/law/$id")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      data: search.data as string | undefined,
    };
  },
});

function RouteComponent() {
  const { data } = Route.useSearch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { lawManagement } = useAccessPermission();
  const [parsedData, setParsedData] = useState({} as Law);

  useEffect(() => {
    if (data) {
      setParsedData(JSON.parse(data));
    }
  }, [data]);

  // Functions
  const deleteLawMutation = useMutation({
    mutationFn: () => deleteLaw(parsedData.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["laws"] });
      toast({
        title: "ສຳເລັດ",
        description: "ລຶບເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ ສໍາເລັດແລ້ວ",
      });
      navigate({ to: "..", replace: true });
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ຂໍອະໄພ, ມີບາງຢ່າງຜິດພາດ",
        variant: "destructive",
      });
    },
  });

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link to=".." replace>
                ຈັດການເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍລະອຽດເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <section className="mt-[32px]">
        <h2 className="text-2xl font-semibold">
          ລາຍລະອຽດເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ{" "}
        </h2>
        <p className="text-muted-foreground">ຂໍ້ມູນເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ </p>
        <Separator className="my-6" />
        <button
          type="button"
          onClick={() =>
            navigate({
              to: "..",
              replace: true,
            })
          }
          className="flex items-center gap-[4px]"
        >
          <ArrowLeft className="w-5 h-5" />
          ກັບຄືນ
        </button>
      </section>
      <section className="max-w-[628px] mx-auto relative space-y-6">
        <div className="space-y-[24px]">
          <div>
            <h3 className="text-[18px] font-semibold">
              ຂໍ້ມູນເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ{" "}
            </h3>
            <p className="text-muted-foreground text-[14px]">
              ລາຍລະອຽດຂໍ້ມູນເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ
            </p>
            <Separator className="my-6" />
          </div>

          <div className="space-y-[24px] ">
            {/* <div className="flex justify-end">
							<Button
								onClick={() =>
									navigate({
										to: "/law/mutate",
										search: {
											id: parsedData.id,
											data: JSON.stringify(parsedData),
										},
									})
								}
								className=" h-[40px] w-[128px]"
							>
								ແກ້ໄຂ
							</Button>
						</div> */}

            <Card>
              <CardContent className="w-full h-[500px] flex flex-col gap-8 items-center justify-center">
                <FileStack className="w-16 h-16 text-primary" />
                {lawManagement.isDownload && (
                  <a
                    href={S3_BUCKET_LINK + parsedData?.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant={"outline"}>ດາວໂຫຼດເອກະສານ</Button>
                  </a>
                )}
              </CardContent>
            </Card>

            <div className="space-y-[12px] text-[14px]">
              <div className="">
                <Label>ຊື່ເອກະສານ</Label>
                <Input value={parsedData?.name} disabled readOnly />
              </div>
              <div className="">
                <Label>ປະເພດຂອງແຈ້ງການ</Label>
                <Input value={parsedData?.lawType?.name} disabled readOnly />
              </div>
            </div>
          </div>

          {/* <div>
						<h3 className="text-[18px] font-semibold">
							ຂໍ້ມູນການອັບເດດຂໍ້ມູນເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ{" "}
						</h3>
						<p className="text-muted-foreground text-[14px]">
							ລາຍລະອຽດຂໍ້ມູນທີ່ບັນທຶກການເຄື່ອນໄຫວຂອງການປ່ຽນແປງຂໍ້ມູນເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ ນີ້
						</p>
						<Separator className="my-6" />

						<div className="space-y-[12px] text-[14px]">
							<div className="flex items-center ">
								<p className="w-[40%]">ວັນທີສ້າງ:</p>
								<span className="flex-1 font-bold">
									{formatDate({
										date: parsedData?.createdAt,
										format: "DD/MM/YYYY",
									})}
								</span>
							</div>
							<div className="flex items-center ">
								<p className="w-[40%]">ສ້າງໂດຍ:</p>
								<span className="flex-1 font-bold">
									{parsedData?.createdBy}{" "}
								</span>
							</div>
							<div className="flex items-center ">
								<p className="w-[40%]">ອັບເດດລ່າສຸດ:</p>
								<span className="flex-1 font-bold">
									{formatDate({
										date: parsedData?.updatedAt,
										format: "DD/MM/YYYY",
									})}
								</span>
							</div>
							<div className="flex items-center ">
								<p className="w-[40%]">ອັບເດດໂດຍ:</p>
								<span className="flex-1 font-bold">
									{parsedData?.updatedBy}
								</span>
							</div>
						</div>
						<p className="text-[14px] mt-[32px]">ລຶບເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ </p>
						<div className="flex items-center justify-between text-[14px]">
							<p>
								ຖ້າທ່ານຕ້ອງການລຶບເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ ສະບັບນີ້ສາມາດກົດ “ລຶບເອກະສານ”
								ປຸ່ມຂວາມືໄດ້ເລີຍ.
							</p>
							<AlertModal
								title="ທ່ານຕ້ອງການລຶບເອກະສານນີ້ແທ້ບໍ?"
								desc={
									<p className="text-[14px] text-muted-foreground">
										ຖ້າທ່ານກົດ <span className="text-foreground">“ຢືນຢັນການລຶບ”</span>{" "}
										ລະບົບຈະເຮັດການ ລຶບເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ ນີ້ທັນທີເຮັດໃຫ້ເອກະສານກົດໝາຍ
										ແລະ ນິຕິກຳ ສະບັບນີ້ບໍ່ສາມາດນຳໃຊ້ໄດ້ອີກ.
									</p>
								}
								triggerComponent={
									<Button
										variant="outline"
										disabled={deleteLawMutation.isPending}
									>
										{deleteLawMutation.isPending
											? "ກຳລັງລຶບເອກະສານກົດໝາຍ ແລະ ນິຕິກຳ ..."
											: "ລຶບເອກະສານ"}
									</Button>
								}
								confirmFunction={() => {
									deleteLawMutation.mutate();
								}}
							/>
						</div>
					</div> */}
        </div>
      </section>
    </>
  );
}
